import type { BlockDefinition, StickyCtaProps } from '../types';
import { ensureHttps, escapeHtml, maybeTokenize, safeUrl } from '../html';

/**
 * Sticky CTA — pinned to the viewport bottom (or top) and stays visible
 * while the visitor scrolls. Conversion booster on long pages where the
 * main CTA scrolls off the screen.
 *
 * Renders as a `position:fixed` bar that overlays the page. To avoid
 * covering the very last block, we add bottom-padding to the body once
 * via inline CSS so the page can scroll past the bar comfortably.
 *
 * `hideAtEnd` mounts a tiny vanilla-JS scroll listener that fades the bar
 * out when the visitor's near the end — feels less aggressive than a
 * permanent overlay.
 */
export const stickyCta: BlockDefinition<StickyCtaProps> = {
  type: 'sticky-cta',
  meta: {
    label: 'Sticky CTA',
    icon: '📌',
    category: 'buttons',
    description: 'Pinned-to-bottom button that stays visible on scroll.',
  },
  defaults: () => ({
    label: 'Get it now',
    url: 'https://',
    position: 'bottom',
    hideAtEnd: true,
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<StickyCtaProps>;
    return {
      label: typeof i.label === 'string' ? i.label.slice(0, 120) : '',
      url: typeof i.url === 'string' ? i.url.slice(0, 2000) : '',
      position: i.position === 'top' ? 'top' : 'bottom',
      hideAtEnd: i.hideAtEnd !== false,
    };
  },
  render(props, _theme, ctx) {
    const href = maybeTokenize(safeUrl(ensureHttps(props.url)), ctx?.tokenizeUrl);
    const safeId = 'pb_sc_' + Math.random().toString(36).slice(2, 10);
    const posStyle =
      props.position === 'top'
        ? 'top:0;border-bottom:1px solid rgba(0,0,0,0.08);'
        : 'bottom:0;border-top:1px solid rgba(0,0,0,0.08);';

    const hideScript = props.hideAtEnd
      ? `<script>(function(){var b=document.getElementById('${safeId}');if(!b)return;function check(){var nearEnd=window.scrollY+window.innerHeight>=document.body.scrollHeight-40;b.style.opacity=nearEnd?'0':'1';b.style.pointerEvents=nearEnd?'none':'auto';}check();window.addEventListener('scroll',check,{passive:true});window.addEventListener('resize',check);})();</script>`
      : '';

    return `<div class="pb-block pb-sticky-cta-spacer" style="height:72px;" aria-hidden="true"></div>
<div id="${safeId}" class="pb-sticky-cta" style="position:fixed;left:0;right:0;${posStyle}padding:12px 16px;background:var(--bg);backdrop-filter:saturate(180%) blur(10px);-webkit-backdrop-filter:saturate(180%) blur(10px);z-index:9999;transition:opacity 0.18s ease;">
  <a href="${href}" target="_blank" rel="noopener noreferrer" style="display:block;max-width:520px;margin:0 auto;padding:14px 20px;border-radius:var(--radius-btn);background:var(--primary);color:#fff;font-size:16px;font-weight:600;text-align:center;text-decoration:none;">${escapeHtml(props.label)}</a>
</div>
${hideScript}`;
  },
};
